import React, { useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { questions } from '../data/questions';
import { categories } from '../data/categories';
import QuestionCard from './QuestionCard';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface AssessmentSectionProps {
  category: number;
  responses: Record<string, number>;
  onResponse: (questionId: string, score: number) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function AssessmentSection({
  category,
  responses,
  onResponse,
  onNext,
  onBack
}: AssessmentSectionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const currentCategory = categories[category];
  const categoryQuestions = questions.filter(q => q.category === currentCategory.id);
  
  const isComplete = categoryQuestions.every(q => typeof responses[q.id] === 'number');

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [category]);

  return (
    <div ref={containerRef} className="space-y-6">
      <div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
          {currentCategory.title}
        </h2>
        <p className="text-sm md:text-base text-gray-600">
          {currentCategory.description}
        </p>
      </div>

      <div className="space-y-4">
        {categoryQuestions.map((question) => (
          <QuestionCard
            key={question.id}
            question={question}
            value={responses[question.id]}
            onChange={(score) => onResponse(question.id, score)}
          />
        ))}
      </div>

      <div className="flex justify-between items-center pt-6">
        <button
          onClick={onBack}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!isComplete}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-[#163B56] hover:bg-[#0D2436] disabled:opacity-50"
        >
          {category === 4 ? 'Continue' : 'Next'}
          <ChevronRight className="w-5 h-5 ml-1" />
        </button>
      </div>
    </div>
  );
}